import { connect, mapProps } from '@formily/react'
import { Input as UIInput } from '@/components/ui/input'
import type { InputHTMLAttributes } from 'react'
import { cn } from '@/lib/utils'

interface InputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  value?: string
  onChange?: (value: string) => void
  addonAfter?: string
  className?: string
}

const InputComponent = ({
  value,
  onChange,
  addonAfter,
  className,
  ...props
}: InputProps) => {
  // Determine input type based on format or pattern for better mobile experience
  let inputType = props.type || 'text'
  const isUrlField =
    (props as any).format === 'uri' || props.pattern?.includes('www\\.')

  // Check for phone field (format: tel or phone pattern)
  if (
    (props as any).format === 'tel' ||
    props.pattern?.includes('[\\+]?[1-9][\\d]')
  ) {
    inputType = 'tel'
  }

  // Check for URL field (format: uri or domain pattern)
  if (isUrlField) {
    inputType = 'url'
  }

  // Check for email field
  if ((props as any).format === 'email') {
    inputType = 'email'
  }

  // Handle URL field auto-completion
  const handleUrlChange = (newValue: string) => {
    if (!isUrlField) {
      onChange?.(newValue)
      return
    }

    const trimmedValue = newValue.trim()

    // Don't auto-add protocol while user is typing
    if (trimmedValue === '' || trimmedValue.startsWith('http')) {
      onChange?.(newValue)
      return
    }

    // Check if it looks like a domain and auto-add https://
    const domainPattern =
      /^(www\.)?[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

    if (domainPattern.test(trimmedValue)) {
      // Only auto-add https:// on blur or when user finishes typing
      onChange?.(newValue)
    } else {
      onChange?.(newValue)
    }
  }

  const handleUrlBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!isUrlField) {
      props.onBlur?.(e)
      return
    }

    const trimmedValue = e.target.value.trim()

    // Don't process if empty, has invalid characters, or already has protocol
    if (
      !trimmedValue ||
      trimmedValue.includes('@') ||
      trimmedValue.includes(' ') ||
      trimmedValue.startsWith('http')
    ) {
      props.onBlur?.(e)
      return
    }

    // Only auto-add https:// to valid domains (must have at least one dot)
    const domainPattern =
      /^(www\.)?[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)+$/

    if (domainPattern.test(trimmedValue)) {
      const urlWithProtocol = `https://${trimmedValue}`
      onChange?.(urlWithProtocol)
    }

    props.onBlur?.(e)
  }

  return (
    <div className={cn('flex items-center gap-2', className)}>
      <UIInput
        {...props}
        type={inputType}
        value={value ?? ''}
        onChange={(e) => handleUrlChange(e.target.value)}
        onBlur={handleUrlBlur}
        className="border-[rgba(255,255,255,0.06)] focus-within:border-[rgba(255,255,255,0.2)]"
      />
      {addonAfter && (
        <span className="text-sm text-muted-foreground whitespace-nowrap">
          {addonAfter}
        </span>
      )}
    </div>
  )
}

export const FormilyInput = connect(
  InputComponent,
  mapProps((props, field) => ({
    ...props,
    value: props.value || '',
    addonAfter: props.addonAfter,
    // Pass through field pattern for input type detection
    pattern: field?.pattern,
    // Get format from field's component props
    format: field?.componentProps?.format,
  })),
)
